import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Generate a comprehensive proctoring report PDF
 * @param {Object} data - Report data
 * @param {Object} data.participant - Participant info (name, email, college)
 * @param {Object} data.challenge - Challenge info (title, company)
 * @param {Object} data.attempt - Attempt info (id, start_time, end_time, status)
 * @param {Object} data.scores - Score info (total, achieved, percentage)
 * @param {Array} data.violations - List of violations with timestamps
 * @param {Array} data.aiEvents - List of AI proctoring events
 * @param {Array} data.screenshots - Base64 screenshots (optional)
 * @param {Object} data.analysis - AI analysis summary
 */
export const generateProctoringReport = (data) => {
  const {
    participant,
    challenge,
    attempt,
    scores,
    violations = [],
    aiEvents = [],
    screenshots = [],
    analysis
  } = data;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPosition = 20;

  // Helper functions
  const addHeader = () => {
    // Header background
    doc.setFillColor(26, 26, 46);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Title
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Proctoring Report', 14, 18);
    
    // Subtitle
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(challenge?.title || 'Enterprise Challenge', 14, 26);
    
    // Company logo placeholder
    doc.setFontSize(8);
    doc.text(challenge?.company || 'Company', pageWidth - 14, 18, { align: 'right' });
    
    // Report date
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 14, 26, { align: 'right' });
    
    // Confidential badge
    doc.setFillColor(220, 38, 38);
    doc.roundedRect(pageWidth - 45, 30, 35, 6, 1, 1, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.text('CONFIDENTIAL', pageWidth - 27.5, 34, { align: 'center' });
    
    yPosition = 50;
  };

  const addSection = (title) => {
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setDrawColor(139, 92, 246);
    doc.setLineWidth(0.5);
    doc.line(14, yPosition, pageWidth - 14, yPosition);
    
    doc.setTextColor(139, 92, 246);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, yPosition + 8);
    
    yPosition += 15;
    doc.setTextColor(60, 60, 60);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
  };

  const addKeyValue = (key, value, indent = 14) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text(key + ':', indent, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);
    doc.text(String(value || '-'), indent + 50, yPosition);
    yPosition += 6;
  };

  // === Build Report ===
  
  // Header
  addHeader();

  // Participant Info Section
  addSection('Participant Information');
  addKeyValue('Name', participant?.name);
  addKeyValue('Email', participant?.email);
  addKeyValue('College', participant?.college);
  addKeyValue('Phone', participant?.phone);
  yPosition += 5;

  // Assessment Summary Section
  addSection('Assessment Summary');
  addKeyValue('Attempt ID', attempt?.id);
  addKeyValue('Start Time', attempt?.start_time ? new Date(attempt.start_time).toLocaleString() : '-');
  addKeyValue('End Time', attempt?.end_time ? new Date(attempt.end_time).toLocaleString() : '-');
  addKeyValue('Duration', attempt?.duration_minutes ? `${attempt.duration_minutes} minutes` : '-');
  addKeyValue('Status', attempt?.status?.toUpperCase());
  yPosition += 5;

  // Scores Section
  addSection('Performance Scores');
  
  // Score visualization
  const scorePercentage = scores?.percentage || 0;
  const scoreColor = scorePercentage >= 70 ? [34, 197, 94] : scorePercentage >= 40 ? [234, 179, 8] : [239, 68, 68];
  
  doc.setFillColor(240, 240, 240);
  doc.roundedRect(14, yPosition, 120, 20, 2, 2, 'F');
  
  doc.setFillColor(...scoreColor);
  doc.roundedRect(14, yPosition, Math.max(5, (scorePercentage / 100) * 120), 20, 2, 2, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`${scorePercentage.toFixed(1)}%`, 70, yPosition + 13, { align: 'center' });
  
  // Score details
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`${scores?.achieved || 0} / ${scores?.total || 0} points`, 140, yPosition + 8);
  doc.text(`Passing: ${scores?.passingPercentage || 40}%`, 140, yPosition + 15);
  
  yPosition += 30;

  // Proctoring Summary Section
  addSection('Proctoring Summary');
  
  const totalViolations = violations.length;
  const aiViolationCount = aiEvents.filter(e => e.event_type !== 'face_detected').length;
  const systemViolations = violations.filter(v => !v.ai_event).length;
  const audioViolations = violations.filter(v => v.type?.startsWith('audio_')).length;
  
  // Violation counts
  addKeyValue('Total Violations', totalViolations);
  addKeyValue('System Violations', systemViolations);
  addKeyValue('AI Violations', aiViolationCount);
  addKeyValue('Audio Violations', audioViolations);
  yPosition += 5;

  // Risk Level
  const riskLevel = totalViolations >= 7 ? 'HIGH' : totalViolations >= 4 ? 'MEDIUM' : 'LOW';
  const riskColor = riskLevel === 'HIGH' ? [239, 68, 68] : riskLevel === 'MEDIUM' ? [234, 179, 8] : [34, 197, 94];
  
  doc.setFillColor(...riskColor);
  doc.roundedRect(14, yPosition, 40, 12, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`${riskLevel} RISK`, 34, yPosition + 8, { align: 'center' });
  
  yPosition += 20;

  // Violations Detail Table
  if (violations.length > 0) {
    addSection('Violation Details');
    
    const violationRows = violations.map((v, i) => [
      i + 1,
      v.type?.replace(/_/g, ' ')?.toUpperCase() || 'Unknown',
      v.severity?.toUpperCase() || 'MEDIUM',
      new Date(v.timestamp).toLocaleTimeString(),
      v.details?.substring(0, 30) || '-'
    ]);

    doc.autoTable({
      startY: yPosition,
      head: [['#', 'Type', 'Severity', 'Time', 'Details']],
      body: violationRows,
      theme: 'striped',
      headStyles: { fillColor: [139, 92, 246], textColor: 255 },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 40 },
        2: { cellWidth: 25 },
        3: { cellWidth: 25 },
        4: { cellWidth: 60 }
      },
      margin: { left: 14, right: 14 }
    });
    
    yPosition = doc.lastAutoTable.finalY + 10;
  }

  // AI Events Summary
  if (aiEvents.length > 0) {
    if (yPosition > 220) {
      doc.addPage();
      yPosition = 20;
    }
    
    addSection('AI Proctoring Events');
    
    // Event type breakdown
    const eventCounts = {};
    aiEvents.forEach(e => {
      const type = e.event_type || 'unknown';
      eventCounts[type] = (eventCounts[type] || 0) + 1;
    });
    
    Object.entries(eventCounts).forEach(([type, count]) => {
      addKeyValue(type.replace(/_/g, ' ').toUpperCase(), `${count} occurrences`);
    });
    
    yPosition += 5;
  }

  // AI Analysis Section
  if (analysis) {
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 20;
    }
    
    addSection('AI Analysis Summary');
    
    doc.setFontSize(9);
    const analysisText = analysis.summary || 'No AI analysis available.';
    const splitText = doc.splitTextToSize(analysisText, pageWidth - 28);
    doc.text(splitText, 14, yPosition);
    yPosition += splitText.length * 5 + 5;
    
    // Confidence indicators
    if (analysis.confidence) {
      addKeyValue('Analysis Confidence', `${(analysis.confidence * 100).toFixed(0)}%`);
    }
    if (analysis.recommendation) {
      yPosition += 5;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(139, 92, 246);
      doc.text('Recommendation:', 14, yPosition);
      yPosition += 6;
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 40);
      const recText = doc.splitTextToSize(analysis.recommendation, pageWidth - 28);
      doc.text(recText, 14, yPosition);
      yPosition += recText.length * 5 + 5;
    }
  }

  // Screenshots Section (if available and not too many)
  if (screenshots.length > 0 && screenshots.length <= 6) {
    doc.addPage();
    yPosition = 20;
    addSection('Violation Screenshots');
    
    const screenshotWidth = 80;
    const screenshotHeight = 60;
    let xPos = 14;
    
    screenshots.slice(0, 6).forEach((screenshot, i) => {
      if (i > 0 && i % 2 === 0) {
        yPosition += screenshotHeight + 10;
        xPos = 14;
      }
      
      try {
        doc.addImage(screenshot, 'JPEG', xPos, yPosition, screenshotWidth, screenshotHeight);
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text(`Violation ${i + 1}`, xPos + screenshotWidth / 2, yPosition + screenshotHeight + 5, { align: 'center' });
      } catch (e) {
        console.error('Error adding screenshot:', e);
      }
      
      xPos += screenshotWidth + 10;
    });
  }

  // Timeline Section
  if (violations.length > 0) {
    doc.addPage();
    yPosition = 20;
    addSection('Violation Timeline');
    
    // Sort violations by timestamp
    const sortedViolations = [...violations].sort((a, b) => 
      new Date(a.timestamp) - new Date(b.timestamp)
    );
    
    const startTime = attempt?.start_time ? new Date(attempt.start_time) : new Date(sortedViolations[0]?.timestamp);
    const endTime = attempt?.end_time ? new Date(attempt.end_time) : new Date();
    const totalDuration = endTime - startTime;
    
    // Draw timeline
    const timelineWidth = pageWidth - 40;
    const timelineY = yPosition + 20;
    
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(2);
    doc.line(20, timelineY, 20 + timelineWidth, timelineY);
    
    // Start and end markers
    doc.setFillColor(34, 197, 94);
    doc.circle(20, timelineY, 3, 'F');
    doc.setFillColor(239, 68, 68);
    doc.circle(20 + timelineWidth, timelineY, 3, 'F');
    
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text('Start', 20, timelineY + 8, { align: 'center' });
    doc.text('End', 20 + timelineWidth, timelineY + 8, { align: 'center' });
    
    // Plot violations
    sortedViolations.forEach((v, i) => {
      const vTime = new Date(v.timestamp);
      const position = ((vTime - startTime) / totalDuration) * timelineWidth;
      
      const color = v.severity === 'high' ? [239, 68, 68] : v.severity === 'medium' ? [234, 179, 8] : [100, 100, 100];
      doc.setFillColor(...color);
      doc.circle(20 + position, timelineY, 2, 'F');
    });
    
    yPosition = timelineY + 20;
    doc.setFontSize(8);
    doc.text(`Total Duration: ${Math.round(totalDuration / 60000)} minutes | ${violations.length} violations recorded`, 14, yPosition);
  }

  // Final Recommendation Section
  doc.addPage();
  yPosition = 20;
  addSection('Final Assessment');
  
  // Overall status box
  const passed = (scores?.percentage || 0) >= (scores?.passingPercentage || 40);
  const trustworthy = totalViolations < 5;
  const finalStatus = passed && trustworthy ? 'APPROVED' : passed && !trustworthy ? 'REVIEW REQUIRED' : 'NOT APPROVED';
  const statusColor = finalStatus === 'APPROVED' ? [34, 197, 94] : finalStatus === 'REVIEW REQUIRED' ? [234, 179, 8] : [239, 68, 68];
  
  doc.setFillColor(...statusColor);
  doc.roundedRect(14, yPosition, pageWidth - 28, 30, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(finalStatus, pageWidth / 2, yPosition + 18, { align: 'center' });
  
  yPosition += 40;
  
  // Recommendation text
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  let recommendation = '';
  if (finalStatus === 'APPROVED') {
    recommendation = 'This candidate has passed the assessment with an acceptable proctoring record. The session showed minimal suspicious activity and the candidate demonstrated genuine problem-solving behavior.';
  } else if (finalStatus === 'REVIEW REQUIRED') {
    recommendation = 'This candidate has passed the assessment but showed some concerning proctoring violations. We recommend manual review of the flagged incidents before making a final hiring decision.';
  } else {
    recommendation = 'This candidate did not pass the assessment criteria. The session showed multiple violations and/or the score was below the passing threshold. Not recommended for advancement.';
  }
  
  const recLines = doc.splitTextToSize(recommendation, pageWidth - 28);
  doc.text(recLines, 14, yPosition);
  
  yPosition += recLines.length * 5 + 15;
  
  // Key metrics summary
  doc.setFont('helvetica', 'bold');
  doc.text('Key Metrics:', 14, yPosition);
  yPosition += 8;
  doc.setFont('helvetica', 'normal');
  
  const metrics = [
    `Score: ${scores?.percentage?.toFixed(1) || 0}% (${passed ? 'PASS' : 'FAIL'})`,
    `Integrity Score: ${Math.max(0, 100 - (totalViolations * 10))}%`,
    `Risk Level: ${riskLevel}`,
    `Total Violations: ${totalViolations}`
  ];
  
  metrics.forEach(m => {
    doc.text(`• ${m}`, 20, yPosition);
    yPosition += 6;
  });
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount} | EvalentX Enterprise Proctoring Report | Confidential`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  return doc;
};

/**
 * Generate and download the proctoring report
 */
export const downloadProctoringReport = (data) => {
  const doc = generateProctoringReport(data);
  const filename = `Proctoring_Report_${data.participant?.name?.replace(/\s+/g, '_') || 'Unknown'}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
};

/**
 * Generate AI analysis summary based on violations
 */
export const generateAIAnalysis = (violations, aiEvents, scores) => {
  const totalViolations = violations.length;
  const aiViolationCount = aiEvents.filter(e => ['no_face', 'multiple_faces', 'looking_away'].includes(e.event_type)).length;
  const audioViolations = violations.filter(v => v.type?.includes('audio')).length;
  const tabSwitches = violations.filter(v => v.type === 'tab_switch').length;
  const copyPaste = violations.filter(v => v.type === 'copy_paste').length;
  const fullscreenExits = violations.filter(v => v.type === 'fullscreen_exit').length;
  
  let summary = '';
  let confidence = 0.85;
  let recommendation = '';
  
  // Generate summary based on patterns
  if (totalViolations === 0) {
    summary = 'The candidate completed the assessment with no proctoring violations. The session appears to be conducted with full integrity. All monitoring systems reported normal activity throughout the test duration.';
    confidence = 0.95;
    recommendation = 'RECOMMEND: Strong candidate for advancement. No integrity concerns detected.';
  } else if (totalViolations <= 3) {
    summary = `The candidate had ${totalViolations} minor violation(s) during the assessment. This may include brief camera obstructions or momentary focus shifts, which can be attributed to normal behavior. No patterns of suspicious activity were detected.`;
    confidence = 0.85;
    recommendation = 'RECOMMEND: Suitable for advancement. Minor violations are within acceptable limits.';
  } else if (totalViolations <= 6) {
    const concerns = [];
    if (aiViolationCount > 2) concerns.push('facial detection anomalies');
    if (tabSwitches > 1) concerns.push('tab switching');
    if (copyPaste > 0) concerns.push('copy-paste attempts');
    if (audioViolations > 1) concerns.push('audio disturbances');
    
    summary = `The candidate showed moderate proctoring concerns with ${totalViolations} violations. Notable issues include: ${concerns.join(', ') || 'various minor infractions'}. While not conclusive evidence of misconduct, these patterns warrant attention.`;
    confidence = 0.75;
    recommendation = 'CAUTION: Consider manual review of flagged incidents before making a hiring decision.';
  } else {
    const majorConcerns = [];
    if (aiViolationCount > 5) majorConcerns.push('consistent camera/face detection issues suggesting possible impersonation or external assistance');
    if (tabSwitches > 3) majorConcerns.push('frequent tab switching indicating potential use of external resources');
    if (audioViolations > 3) majorConcerns.push('significant audio activity suggesting possible verbal communication with others');
    if (fullscreenExits > 2) majorConcerns.push('multiple fullscreen exits indicating possible screen sharing or reference to external materials');
    
    summary = `HIGH ALERT: The candidate demonstrated ${totalViolations} proctoring violations during the assessment, significantly exceeding normal thresholds. Major concerns include: ${majorConcerns.join('; ') || 'multiple types of suspicious activity'}. The pattern of violations suggests potential academic dishonesty.`;
    confidence = 0.90;
    recommendation = 'NOT RECOMMENDED: High risk of assessment integrity compromise. Suggest disqualification or re-assessment under stricter conditions.';
  }
  
  return {
    summary,
    confidence,
    recommendation,
    metrics: {
      totalViolations,
      aiViolations: aiViolationCount,
      audioViolations,
      tabSwitches,
      copyPasteAttempts: copyPaste,
      fullscreenExits
    }
  };
};

export default {
  generateProctoringReport,
  downloadProctoringReport,
  generateAIAnalysis
};
